// Ecommerce hooks (R-075). Availability is server-derived from production
// planning — the grid refetches with orders so the numbers never go stale
// against a booking.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type { AvailabilityResult, CustomerRecord, SalesOrder, SkuRecord } from './ecommerce.types';

const EC = '/users/v1/ecommerce';
const KEY = ['ecommerce'] as const;

/** A refresh somebody asked for on the Sales screen: refetches every ACTIVE sales read (orders,
 *  availability, catalogue, customers). Orders poll every 30 s, availability every 60 s. */
export function useRefreshSales() {
  const qc = useQueryClient();
  return () => qc.refetchQueries({ queryKey: KEY, type: 'active' });
}

export function useSkus() {
  return useQuery({ queryKey: [...KEY, 'skus'], queryFn: () => api.get<SkuRecord[]>(`${EC}/skus`) });
}

export function useUpsertSku() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: {
      id?: string;
      code: string;
      crop_id: string;
      variety_id?: string;
      name_de: string;
      name_en: string;
      package_g: number;
      price_cents: number;
      currency?: string;
      min_order_units: number;
      shelf_life_days: number;
      active?: boolean;
    }) => api.post<SkuRecord>(`${EC}/skus`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCustomers() {
  return useQuery({
    queryKey: [...KEY, 'customers'],
    queryFn: () => api.get<CustomerRecord[]>(`${EC}/customers`),
  });
}

export function useUpsertCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: { id?: string; name: string; contact?: string; address?: string; active?: boolean }) =>
      api.post<CustomerRecord>(`${EC}/customers`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAvailability(skuId: string | null, fromDate: string, weeks = 8) {
  return useQuery({
    queryKey: [...KEY, 'availability', skuId, fromDate, weeks],
    queryFn: () =>
      api.get<AvailabilityResult>(`${EC}/availability?sku_id=${skuId}&from_date=${fromDate}&weeks=${weeks}`),
    enabled: !!skuId && !!fromDate,
    refetchInterval: 60_000,
  });
}

export function useSalesOrders(status: string) {
  return useQuery({
    queryKey: [...KEY, 'orders', status],
    queryFn: () => api.get<SalesOrder[]>(`${EC}/orders?status=${status}`),
    refetchInterval: 30_000,
    // A filter change keeps the current rows (flagged `isPlaceholderData`) while
    // the new status loads, so the list dims instead of vanishing.
    placeholderData: keepPreviousData,
  });
}

export function useCreateSalesOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: {
      site_id: string;
      customer_id: string;
      sku_id: string;
      qty_units: number;
      deliver_from: string;
      deliver_to: string;
      note?: string;
    }) => api.post<SalesOrder>(`${EC}/orders`, r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Availability re-check + production-demand handover; over-booking = 409. */
export function useConfirmSalesOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<SalesOrder>(`${EC}/orders/${id}/confirm`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCancelSalesOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<boolean>(`${EC}/orders/${id}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
